import React, { useEffect, useState, useRef } from 'react';

import {
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { FORM_CLEAR_ERROR, FORM_SET_ERROR } from '@/providers/actionTypes';
import { useFormErrorDispatch } from '@/providers/FormErrorContext';
import { useAuth } from '@/providers/AuthContext';
import MarkdownRenderer from '@/components/shared/MarkdownRenderer';
import type { SetValuesCallback, DocumentState } from '@/types';

interface TermsAndConditionsProps {
  setValues: SetValuesCallback;
}

function TermsAndConditions({ setValues }: TermsAndConditionsProps) {
  const componentRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const formErrorDispatch = useFormErrorDispatch();

  // Get the list of documents based on on_prem flag
  const showEula = auth.config?.on_prem === true;
  const documentTypes: string[] = ['tos', 'privacy', ...(showEula ? ['eula'] : [])];

  const [documents, setDocuments] = useState<Record<string, DocumentState>>(() => {
    const initialDocs: Record<string, DocumentState> = {
      tos: { accepted: false, content: '', loading: true },
      privacy: { accepted: false, content: '', loading: true },
    };
    if (showEula) {
      initialDocs['eula'] = { accepted: false, content: '', loading: true };
    }
    return initialDocs;
  });
  const [currentDocument, setCurrentDocument] = useState<string>('tos');

  // Update documents state when showEula changes (e.g., when auth.config loads)
  useEffect(() => {
    if (auth.config !== null) {
      setDocuments((prevDocs) => {
        const newDocs = { ...prevDocs };

        if (showEula && !newDocs['eula']) {
          // Add EULA to documents if it should be shown
          newDocs['eula'] = { accepted: false, content: '', loading: true };
        } else if (!showEula && newDocs['eula']) {
          // Remove EULA from documents if it shouldn't be shown
          delete newDocs['eula'];
        }

        return newDocs;
      });
    }
  }, [showEula, auth.config]);

  // Load all documents immediately on component mount or when showEula changes
  useEffect(() => {
    const loadAllDocuments = async () => {
      try {
        // Build fetch promises based on whether EULA should be shown
        const fetchPromises = [fetch('/md/tos.md'), fetch('/md/privacy.md')];

        if (showEula) {
          fetchPromises.push(fetch('/md/eula.md'));
        }

        const responses = await Promise.all(fetchPromises);
        const texts = await Promise.all(
          responses.map((response) => response.text()),
        );

        const updatedDocuments: Record<string, DocumentState> = {
          tos: { accepted: false, content: texts[0], loading: false },
          privacy: { accepted: false, content: texts[1], loading: false },
        };

        if (showEula) {
          updatedDocuments['eula'] = {
            accepted: false,
            content: texts[2],
            loading: false,
          };
        }

        setDocuments(updatedDocuments);
      } catch (error) {
        console.error('Error loading documents:', error);
        const errorDocuments: Record<string, DocumentState> = {
          tos: { accepted: false, content: '', loading: false },
          privacy: { accepted: false, content: '', loading: false },
        };
        if (showEula) {
          errorDocuments['eula'] = {
            accepted: false,
            content: '',
            loading: false,
          };
        }
        setDocuments(errorDocuments);
      }
    };

    // Only load if auth.config is available, or after a short delay to allow config to load
    if (auth.config !== null) {
      loadAllDocuments();
    } else {
      // If config hasn't loaded yet, set a timeout to retry
      const timeoutId = setTimeout(() => {
        if (auth.config !== null) {
          loadAllDocuments();
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [auth.config, showEula]);

  // Update form values when acceptance status changes
  useEffect(() => {
    const allAccepted = Object.values(documents).every((doc) => doc.accepted);
    setValues({
      terms_accepted: allAccepted,
    });
  }, [documents, setValues]);

  // Update form error state
  useEffect(() => {
    const allAccepted = Object.values(documents).every((doc) => doc.accepted);
    if (!allAccepted) {
      formErrorDispatch({ type: FORM_SET_ERROR, payload: true });
    } else {
      formErrorDispatch({ type: FORM_CLEAR_ERROR });
    }
  }, [documents, formErrorDispatch]);

  const handleAccept = (docType: string) => {
    setDocuments((prev) => ({
      ...prev,
      [docType]: { ...prev[docType], accepted: true },
    }));

    // Move to next document if current one is accepted
    const currentIndex = documentTypes.indexOf(docType);
    if (currentIndex < documentTypes.length - 1) {
      // Scroll to top FIRST, then change document
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setCurrentDocument(documentTypes[currentIndex + 1]);
    }
  };

  const handleAccordionChange = (event: React.SyntheticEvent) => {
    // Prevent default accordion behavior that might cause scrolling
    event.preventDefault();
    event.stopPropagation();
  };

  const getDocumentTitle = (docType: string): string => {
    const titles: Record<string, string> = {
      tos: 'Terms of Service',
      privacy: 'Privacy Policy',
      eula: 'End User License Agreement (EULA)',
    };
    return titles[docType] || docType;
  };

  const getDocumentIcon = () => {
    return <ExpandMoreIcon />;
  };

  return (
    <Box ref={componentRef} width='100%' padding='15px'>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box>
            <Typography variant='h6' gutterBottom sx={{ mb: 3 }}>
              Terms and Conditions
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
              Please review and accept each agreement to proceed. You must
              accept them in order.
            </Typography>

            {documentTypes.map((docType, index) => {
              const doc = documents[docType];
              const isExpanded = currentDocument === docType && !doc.accepted;
              const isDisabled =
                index > 0 && !documents[documentTypes[index - 1]].accepted;

              return (
                <Box key={docType} sx={{ mb: 3 }}>
                  <Accordion
                    expanded={isExpanded}
                    disabled={isDisabled}
                    onChange={handleAccordionChange}
                    sx={{
                      mb: 2,
                      '&.Mui-disabled': {
                        backgroundColor: 'grey.100',
                        opacity: 0.6,
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 16px 0',
                      },
                      // Prevent accordion from affecting scroll position
                      '& .MuiAccordionSummary-root': {
                        '&:focus': {
                          outline: 'none',
                        },
                        '&:focus-visible': {
                          outline: 'none',
                        },
                      },
                      // Prevent scroll jumping when accordion expands
                      '& .MuiCollapse-root': {
                        '&.MuiCollapse-entered': {
                          overflow: 'visible',
                        },
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={getDocumentIcon()}
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Typography variant='h6'>
                          {getDocumentTitle(docType)}
                        </Typography>
                        {isDisabled && (
                          <Chip
                            label='Complete previous document first'
                            color='default'
                            size='small'
                          />
                        )}
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 3,
                          mb: 3,
                          backgroundColor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        {doc.loading ? (
                          <Typography>Loading document...</Typography>
                        ) : (
                          <MarkdownRenderer content={doc.content} />
                        )}
                      </Paper>
                    </AccordionDetails>
                  </Accordion>

                  {/* Always render all document contents but hide them */}
                  {!isExpanded && !doc.loading && (
                    <Box sx={{ display: 'none' }}>
                      <MarkdownRenderer content={doc.content} />
                    </Box>
                  )}

                  <FormControlLabel
                    required
                    control={
                      <Checkbox
                        checked={doc.accepted}
                        onChange={() => handleAccept(docType)}
                        disabled={doc.loading || isDisabled}
                      />
                    }
                    label={`I accept the ${getDocumentTitle(docType)} above.`}
                    sx={{ ml: 0 }}
                  />
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                mt: 2,
                p: 3,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                <strong>Progress:</strong>{' '}
                {Object.values(documents).filter((doc) => doc.accepted).length}{' '}
                of {Object.keys(documents).length} agreements accepted
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TermsAndConditions;

